<template>
  <div class="overview-constraints-panel flex flex-col gap-4 min-w-0 mb-6" data-testid="overview-constraints-panel">
    <div class="flex items-center justify-between gap-2 flex-wrap">
      <h2 class="overview-section-title m-0">
        {{ $t('UserProjects.overview.constraints.title') }}
      </h2>
      <button
        type="button"
        class="overview-constraints-panel__report-btn"
        data-testid="overview-constraints-report"
        @click="goToCreate"
      >
        <i class="pi pi-plus text-xs" aria-hidden="true" />
        {{ $t('UserProjects.overview.constraints.report') }}
      </button>
    </div>

    <div
      v-if="loading"
      class="flex items-center justify-center py-8 text-[var(--color-disabled)]"
    >
      <i class="pi pi-spin pi-spinner mr-2" />
      {{ $t('UserProjects.overview.loading') }}
    </div>

    <div
      v-else-if="activeConstraints.length === 0"
      class="overview-constraints-panel__empty text-sm text-[var(--color-disabled)] py-8 text-center"
      data-testid="overview-constraints-empty"
    >
      {{ $t('UserProjects.constraints.emptyHint') }}
    </div>

    <div v-else class="flex flex-col gap-2">
      <button
        v-for="item in activeConstraints"
        :key="item.id"
        type="button"
        class="overview-constraint-row text-left"
        data-testid="overview-constraint-item"
        :data-constraint-type="item.type"
        @click="goToConstraints"
      >
        <div class="flex items-center gap-2 mb-1 min-w-0">
          <span
            class="overview-constraint-priority shrink-0"
            :class="priorityFor(item).dotClass"
            aria-hidden="true"
          />
          <span class="text-xs font-medium" :class="priorityTextClass(item)">
            {{ $t(priorityFor(item).labelKey) }}
          </span>
          <span class="overview-constraint-row__category ml-auto shrink-0">
            {{ categoryLabel(item) }}
          </span>
        </div>
        <p class="text-sm text-[var(--color-dark)] m-0">{{ displayTitle(item) }}</p>
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { getConstraintPriority } from '@/utils/constraintDisplay';
import { displayConstraintTitle } from '@/utils/constraintFormMapping';

const CATEGORY_SHORT = {
  external_decision: 'EXTERN',
  internal_decision: 'INTERN',
  external_event: 'EXTERN',
  capacity: 'RESSOURCE',
  missing_info: 'INFO',
  dependency: 'ABHÄNGIGKEIT',
  technical_blocker: 'TECHNISCH',
  other: 'SONSTIGE',
  blocked: 'BLOCKIERT',
  resource: 'RESSOURCE',
  scope: 'SCOPE',
};

export default {
  name: 'OverviewConstraintsPanel',
  computed: {
    ...mapGetters('pmCommitment', {
      overview: 'overview',
      loading: 'overviewLoading',
    }),
    activeConstraints() {
      return this.overview?.activeConstraints || [];
    },
  },
  methods: {
    priorityFor(item) {
      return getConstraintPriority(item);
    },
    priorityTextClass(item) {
      const key = this.priorityFor(item).key;
      if (key === 'high') return 'text-[#991b1b]';
      if (key === 'medium') return 'text-[#854d0e]';
      return 'text-[#6b6b6b]';
    },
    categoryLabel(item) {
      if (item.categoryLabel) return item.categoryLabel;
      const key = item.category || item.type;
      return CATEGORY_SHORT[key] || String(key || '').toUpperCase();
    },
    displayTitle(item) {
      return displayConstraintTitle(item);
    },
    goToCreate() {
      this.$router.push({ path: '/user-projects/constraints', query: { create: '1' } });
    },
    goToConstraints() {
      this.$router.push('/user-projects/constraints');
    },
  },
};
</script>

<style scoped>
.overview-section-title {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.3;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  word-break: break-word;
  min-width: 0;
}

.overview-constraints-panel__empty {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
}

.overview-constraints-panel__report-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: var(--color-dark);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  white-space: normal;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
}

.overview-constraints-panel__report-btn:hover {
  background: #f5f5f5;
}

.overview-constraint-row {
  width: 100%;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 14px;
  padding: 0.75rem;
  cursor: pointer;
  transition: box-shadow 0.15s ease;
}

.overview-constraint-row:hover {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.overview-constraint-priority {
  width: 8px;
  height: 8px;
  border-radius: 999px;
}

.overview-constraint-priority--high {
  background: #ef4444;
}

.overview-constraint-priority--medium {
  background: #eab308;
}

.overview-constraint-priority--low {
  background: #ababab;
}

.overview-constraint-row__category {
  font-size: 10px;
  text-transform: uppercase;
  padding: 0.125rem 0.375rem;
  border-radius: 999px;
  background: #f2f2f2;
  color: #6b6b6b;
  overflow-wrap: anywhere;
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
}
</style>
